import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@apollo/client';
import { gql } from '@/gql';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { Logo } from '../../components/logos/logo';

const GET_MERCHANT_ID = gql(/* GraphQL */ `
  query GetMerchantId {
    currentMerchant {
      id
      domain
    }
  }
`);

interface CompleteSetupProps {
  /**
   * Text to display on the footer button.
   * Defaults to 'Complete Setup'.
   */
  buttonText?: string;

  /**
   * Click handler for the footer button.
   * If not provided, defaults to marking the step as completed and navigating to the home page.
   */
  onButtonClick?: () => void;

  /**
   * Controls the visibility of the footer section.
   * Defaults to true.
   */
  showFooter?: boolean;
}

export function CompleteSetup({
  buttonText = 'Complete Setup',
  onButtonClick,
  showFooter = true
}: CompleteSetupProps) {
  const navigate = useNavigate();
  const { orgSlug } = useAuth();
  const { data } = useQuery(GET_MERCHANT_ID);
  const merchantId = data?.currentMerchant.id.split('.')[0] ?? '';
  const onlineStoreUrl = merchantId ? `https://admin.shopify.com/store/${merchantId}/themes` : '';

  const handleButtonClick = () => {
    if (onButtonClick) {
      onButtonClick();
    } else {
      if (orgSlug) {
        void navigate(`/merchants/${orgSlug}/dashboard`);
      }
    }
  };

  return (
    <Card className="w-full max-w-xl mx-4 sm:mx-auto p-9 rounded-[1rem]">
      <ArrowLeft
        className="cursor-pointer"
        onClick={() => {
          void navigate(-1);
        }}
      />
      <CardHeader className="items-center space-y-8 p-0">
        <Logo icon="contextual" width={36} height={36} />
        <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-medium">
          Connect your store
        </h1>
      </CardHeader>
      <CardContent className="space-y-6 my-5 sm:my-9 p-0">
        <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold">{`Enable Embedded Script in Shopify`}</h3>
        <Button
          variant="secondary"
          className="mt-2"
          onClick={() => window.open(onlineStoreUrl, '_blank')}
        >
          Go to Shopify Settings
          <ExternalLink className="ml-2 h-4 w-4" />
        </Button>
        <p className="text-sm text-gray-600 mt-2">
          In Shopify, go to
          <span className="font-semibold text-sm text-black">{` Online Store > Customize > App Embeds `}</span>
          and turn on the enable toggle, and then click save.
        </p>
      </CardContent>
      {showFooter && (
        <CardFooter className="flex justify-center space-x-8">
          <Button onClick={handleButtonClick}>{buttonText}</Button>
          <Button variant="secondary" className="">
            Cancel
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}
